import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import axios from 'axios';
import { googleMapLoader } from '../../../helpers/googleMapLoader';
import debounce from '../../../helpers/debounce';
import messages from '../../../locale/jsonMessages';
import { autoCompleteURL, placeDetailURL } from '../../../config';
import { useSelector } from 'react-redux';
import { decode } from '../../../helpers/queryEncryption';

const PlaceGeoSuggest = ({
    id,
    className,
    containerClassName,
    placeholder,
    initialValue,
    onChange,
    onSuggestSelect
}) => {
    const { formatMessage } = useIntl();
    const [googleMapsApiLoaded, setGoogleMapsApiLoaded] = useState(false);
    const googleMapBrowserApiKey = useSelector(state => state?.encodeConfigSettings?.googleMapBrowserApiKey);
    const [value, setValue] = useState(initialValue);
    const [suggestion, setSuggestion] = useState([]);
    const dropdownRef = useRef(null);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        let mounted = true;

        const loadGoogleMaps = async () => {
            await googleMapLoader(googleMapBrowserApiKey, 'places');
            if (mounted) {
                setGoogleMapsApiLoaded(true);
            }
        };

        loadGoogleMaps();

        const isDocument = typeof document !== 'undefined';
        if (isDocument) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            mounted = false;
            if (isDocument) {
                document.removeEventListener('mousedown', handleClickOutside);
                document.removeEventListener('touchstart', handleClickOutside);
            }
        };
    }, []);

    const handleClickOutside = (event) => {
        if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
            setSuggestion([]);
            const parentSection = document.getElementById('safariFailureGeoSuggestionDropDown' + id);
            const childSection = document.getElementById('suggestItem' + id);
            if (parentSection && childSection) {
                parentSection.removeChild(childSection);
            }
        }
    };

    const handleAutoComplete = debounce(async (e) => {
        const value = e?.target?.value;
        onChange && onChange(value);
        if (value?.length > 0) {
            const requestData = {
                input: value
            };
            axios.post(
                autoCompleteURL,
                requestData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Goog-Api-Key': decode(googleMapBrowserApiKey)
                    }
                }
            ).then(response => {
                if (response?.data?.suggestions?.length > 0) {
                    setSuggestion(response?.data?.suggestions);
                }
            }).catch(error => {
                console.error('Error:', error?.response ? error?.response?.data : error?.message);
            });
        }
    }, 100);

    const handleSuggest = async ({ placeId, value }) => {
        if (placeId) {
            try {
                const response = await axios.get(`${placeDetailURL}${placeId}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Goog-Api-Key': decode(googleMapBrowserApiKey),
                        'X-Goog-FieldMask': '*'
                    }
                });
                if (response?.data) {
                    onSuggestSelect(response?.data, value);
                    setValue(value);
                    setSuggestion([]);
                }
            } catch (error) {
                console.error('Place Details Error:', error?.response?.data || error?.message);
            }
        }
    };

    const handleGeoSuggestFailure = (e) => {
        const suggestionsContainer = document.getElementById('safariFailureGeoSuggestionDropDown' + id);
        suggestionsContainer.style.display = "block";
        let suggestionItem = document.createElement("div");
        suggestionItem.id = 'suggestItem' + id;
        suggestionItem.classList.add('suggestItem')
        suggestionItem.textContent = formatMessage(messages.locationWarning);
        if (e.target.value != "") {
            if (!document.getElementById('suggestItem' + id)) {
                suggestionsContainer.appendChild(suggestionItem);
            }
        } else {
            const parentSection = document.getElementById('safariFailureGeoSuggestionDropDown' + id);
            const childSection = document.getElementById('suggestItem' + id);
            if (parentSection && childSection) {
                parentSection.removeChild(childSection);
            }
        }
    };

    return (
        <div className={containerClassName} ref={dropdownRef}>
            {googleMapsApiLoaded ? (
                <div className='manualGeoSuggestContainer'>
                    <input
                        id={id}
                        type='text'
                        className='manualGeoSuggestInput widthFull'
                        placeholder={placeholder}
                        value={value}
                        autoComplete='off'
                        onChange={(e) => {
                            setValue(e?.target?.value);
                            if (e?.target?.value?.length == 0) setSuggestion([]);
                            handleAutoComplete(e);
                        }}
                    />
                    {suggestion?.length > 0 && (
                        <ul className='manualGeoSuggestListWrapper'>
                            {suggestion?.map((item, itemIndex) => (
                                <li
                                    key={itemIndex}
                                    className='manualGeoSuggestList'
                                    onClick={() => handleSuggest({
                                        placeId: item?.placePrediction?.placeId,
                                        value: item?.placePrediction?.text?.text
                                    })}
                                >
                                    {item?.placePrediction?.text?.text}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            ) : (
                <div className='manualGeoSuggestContainer'>
                    <input
                        type='text'
                        className='manualGeoSuggestInput widthFull'
                        placeholder={placeholder}
                        value={value}
                        autoComplete='off'
                        onChange={handleGeoSuggestFailure}
                    />
                    <div id={'safariFailureGeoSuggestionDropDown' + id} className='safariManualGeoSuggestListWrapper' />
                </div>
            )}
        </div>
    );
};

PlaceGeoSuggest.propTypes = {
    id: PropTypes.any,
    className: PropTypes.string,
    containerClassName: PropTypes.string,
    placeholder: PropTypes.string,
    initialValue: PropTypes.string,
    onChange: PropTypes.func,
    onSuggestSelect: PropTypes.func
};

export default PlaceGeoSuggest;